import React from 'react';
import { Wand2, SendHorizontal } from 'lucide-react';
import { useNodeExplorerStore } from '@/stores/node-explorer-store';
import { useNodeStore, selectSelectedNodeIds } from '@/stores/node-store';
import { useShallow } from 'zustand/react/shallow';
import { getStructDisplayLabel } from '@/stores/helpers/node-naming';
import { isCondensedLayout } from '@/constants/workflow-editor';

interface ChatShortcutBarProps {
  panelGroupWidth?: number;
}

export const ChatShortcutBar: React.FC<ChatShortcutBarProps> = ({
  panelGroupWidth,
}) => {
  const isCondensed = isCondensedLayout(panelGroupWidth);
  const { openChatTab, isMaximized } = useNodeExplorerStore();
  const nodes = useNodeStore((state) => state.nodes);
  const selectedNodeIds = useNodeStore(useShallow(selectSelectedNodeIds));

  const firstSelectedId = selectedNodeIds[0] ?? null;
  const selectedNode = firstSelectedId
    ? nodes.find((node) => node.id === firstSelectedId)
    : undefined;
  const selectedNodeName = getStructDisplayLabel(selectedNode?.data?.struct);

  // Determine chat label based on selection
  const chatLabel = selectedNodeName
    ? `Chat with ${selectedNodeName}`
    : 'Chat with the workflow';

  // Don't render if node explorer panel is open
  if (isMaximized) {
    return null;
  }

  if (isCondensed) {
    return (
      <button
        onClick={openChatTab}
        className='group flex items-center rounded-lg shadow-md border border-border bg-card hover:bg-muted cursor-pointer overflow-hidden'
        style={{ padding: '6px' }}
      >
        <div
          className='flex items-center gap-1.5 rounded-sm'
          style={{ padding: '10px', height: '34px' }}
        >
          <Wand2 className='w-5 h-5 text-muted-foreground group-hover:text-foreground shrink-0' />
          <span className='text-sm text-muted-foreground group-hover:text-foreground'>
            Chat
          </span>
        </div>
      </button>
    );
  }

  return (
    <button
      onClick={openChatTab}
      className='group flex items-center rounded-lg shadow-md border border-border bg-card hover:bg-muted cursor-pointer overflow-hidden'
      style={{
        width: '340px',
        padding: '6px',
      }}
    >
      {/* Inner input container */}
      <div
        className='flex items-center gap-2 flex-1 rounded-sm min-w-0 overflow-hidden'
        style={{
          padding: '10px',
          height: '34px',
        }}
      >
        <Wand2 className='w-5 h-5 text-muted-foreground group-hover:text-foreground shrink-0' />
        <span className='text-sm text-muted-foreground group-hover:text-foreground flex-1 text-left truncate min-w-0'>
          {chatLabel}
        </span>
        <SendHorizontal className='w-5 h-5 text-muted-foreground group-hover:text-foreground shrink-0' />
      </div>
    </button>
  );
};

export default ChatShortcutBar;
